/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { JasperServerStatus } from '@gh/builder-shared-data';
import {
  createDialogRoute,
  EnumDictionary,
  getDialogInitData,
  ManualPollProgress,
  manualPollProgress,
} from '@gh/common-shared';
import {
  DialogInput,
  DialogService,
  EmptyDialogInput,
  noAnswer,
  NotificationService,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import { filter$, switchMap$ } from '@gh/rx/operators';
import { JasperServer, JcDetailsDialogBusinessService } from '../../resources/jc-details-dialog-business.service';
import { BuilderUserContext } from '@gh/builder-shared';

@Component({
  selector: 'gh-jc-details-dialog',
  template: require('./jc-details-dialog.component.html'),
  styles: [require('./jc-details-dialog.component.scss')],
})
export class JcDetailsDialogComponent implements OnInit, OnDestroy, DialogInput<EmptyDialogInput> {
  input: EmptyDialogInput;
  details: JasperServer;
  jasperServerStatusDict: EnumDictionary<JasperServerStatus>;
  isToggled: boolean;
  isDisabled: boolean;
  private progress: ManualPollProgress<JasperServer>;

  constructor(@Inject(MAT_DIALOG_DATA) private dialogData: any,
              private business: JcDetailsDialogBusinessService,
              private dialogs: DialogService,
              private waitings: WaitingService,
              private notifications: NotificationService) {

  }

  get isActive(): boolean {
    return this.details.status === JasperServerStatus.Active;
  }

  get isInactive(): boolean {
    return this.details.status === JasperServerStatus.Inactive;
  }

  get serverUrl(): Maybe<string> {
    return this.isActive ? this.details.url : void 0;
  }

  ngOnInit(): void {
    const { jasperServerStatusDict, details } = getDialogInitData(this.dialogData);

    this.jasperServerStatusDict = jasperServerStatusDict;

    this.progress = manualPollProgress({
      poll: () => this.business.status(),
      isInProgress: ({ status }) => status === JasperServerStatus.InProcess,
    });

    this.progress.onNext((updatedDetails) => this.refresh(updatedDetails));

    this.refresh(details);
  }

  ngOnDestroy(): void {
    this.progress.finish();
  }

  onToggle(toggled: boolean): void {
    this.isToggled = toggled;

    if (toggled) {
      this.business.turnOn().pipe(
        this.waitings.operator())
        .subscribe(
          (details) => this.refresh(details),
          this.notifications.processAll);
    } else {
      const confirmation$ = this.dialogs.confirmation(yesNoConfirmation('msg_jc_switch_off'));

      confirmation$.pipe(
        filter$(yesAnswer),
        switchMap$(() => this.business.turnOff().pipe(this.waitings.operator())))
        .subscribe(
          (details) => this.refresh(details),
          this.notifications.processAll);

      confirmation$.pipe(
        filter$(noAnswer))
        .subscribe(() => this.refresh(this.details));
    }
  }

  private refresh(details: JasperServer): void {
    this.details = details;
    this.isToggled = this.details.status === JasperServerStatus.Active;
    this.isDisabled = this.details.status === JasperServerStatus.InProcess;
    this.progress.set(details);
  }
}

export const jcDetailsDialogRoute = createDialogRoute({
  component: JcDetailsDialogComponent,
  init: JcDetailsDialogBusinessService,
});
